import React, { useEffect, useRef, useState } from 'react';
import { Observer } from 'mobx-react';
import { useTheme } from '@mui/material/styles';
import { useBreakpointDown } from '@shared/hooks/useBreakpoint';

import { useStyles } from './ExecutionAnalytics.styles';

import { ThemeProps } from '@styles/theme';
import { Typography } from '@mui/material';
import { ExecutionSummary } from '../ExecutionSummary';
import { ExecutionDetails } from '../ExecutionDetails';
import { useExecutionsUIStore, useGradingsUIStore } from '@core/useStores';
import { GradingTab } from '../GradingTab';

export const ExecutionAnalytics: React.FC = () =>{
    const theme = useTheme<ThemeProps>();
    const styles = useStyles({ theme });
    const isNarrowLayout = useBreakpointDown('tablet');

    const [selectedTab, setSelectedTab] = useState<string>('behaviour');
    const [tabInitializedForExecutionId, setTabInitializedForExecutionId] = useState<string | null>(null);
    const manualTabSelectionRef = useRef(false);

    const executionsUIStore = useExecutionsUIStore();
    const gradingsUIStore = useGradingsUIStore();
    const activeExecution = executionsUIStore.execution?.id ? executionsUIStore.execution : executionsUIStore._execution;

    const hasObservableBehaviourData = () => {
        const hasReviewedGrading = gradingsUIStore.gradingFound && gradingsUIStore.hasReviewedGrading;
        const hasReviewBehaviours = Boolean(gradingsUIStore.groupedBehaviours?.formattedComps?.length);

        if (hasReviewedGrading) {
            return true;
        }

        const isCorrectExecutionMode = ['assessment', 'feedback'].includes(activeExecution?.executionMode?.toLowerCase() ?? '');

        if (!isCorrectExecutionMode) {
            return hasReviewBehaviours;
        }

        const hasExecutionContext = Boolean(
            activeExecution?.id ||
            activeExecution?.entityName ||
            activeExecution?.entityId ||
            activeExecution?.entityVersion
        );

        const fallbackBehaviours = hasExecutionContext
            ? gradingsUIStore.loadOBsVariation(activeExecution.entityName ?? activeExecution).length
            : 0;

        return hasReviewBehaviours || fallbackBehaviours > 0;
    };

    useEffect(() => {
        if (executionsUIStore.selectedExecutionId !== tabInitializedForExecutionId) {
            setTabInitializedForExecutionId(null);
            manualTabSelectionRef.current = false;
        }
    }, [executionsUIStore.selectedExecutionId, tabInitializedForExecutionId]);

    useEffect(() => {
        const hasObservableBehaviours = hasObservableBehaviourData();

        if (!hasObservableBehaviours && selectedTab === 'behaviour') {
            setSelectedTab('summary');
        }
    }, [selectedTab, executionsUIStore.selectedExecutionId, gradingsUIStore.gradingFound]);

    useEffect(() => {
        if (!executionsUIStore.selectedExecutionId || tabInitializedForExecutionId === executionsUIStore.selectedExecutionId || !executionsUIStore.status.isDone || !gradingsUIStore.status.isDone) {
            return;
        }

        if (manualTabSelectionRef.current) {
            setTabInitializedForExecutionId(executionsUIStore.selectedExecutionId);

            return;
        }

        setSelectedTab(hasObservableBehaviourData() ? 'behaviour' : 'summary');
        setTabInitializedForExecutionId(executionsUIStore.selectedExecutionId);
    }, [executionsUIStore.selectedExecutionId, activeExecution?.entityName, activeExecution?.entityVersion, activeExecution?.entityId, activeExecution?.entityAdditionalData, activeExecution?.steps, executionsUIStore.status.isDone, gradingsUIStore.status.isDone, gradingsUIStore.gradingFound, tabInitializedForExecutionId]);

    const onTabSelect = (tab: string) => {
        manualTabSelectionRef.current = true;
        setSelectedTab(tab);
    };

    return (
        <>
            <Observer>
                {() =>(
                    <>
                        {executionsUIStore.status.isDone &&
                                (() => {
                                    const hasObservableBehaviours = hasObservableBehaviourData();
                                    const availableTabs = [
                                        ...(hasObservableBehaviours ? ['behaviour'] : []),
                                        'summary',
                                        'details',
                                    ];
                                    const resolvedTab = availableTabs.includes(selectedTab) ? selectedTab : availableTabs[0];

                                    return (
                                        <div className={styles.mainWrapper}>
                                            <div className={styles.header}>
                                                <div className={styles.tabsContainer}>
                                                    {hasObservableBehaviours &&
                                            <button className={styles.tab} onClick={() => onTabSelect('behaviour')}
                                                style={{
                                                    borderBottom: resolvedTab == 'behaviour' ? `3px solid var(--Stroke-Action, ${theme.colors.violet_primary})` : '',
                                                    backgroundColor: resolvedTab == 'behaviour' ? `var(--Components-Tab-Selected, ${theme.colors.tab_white})` : '',
                                                }}
                                            >
                                                <Typography variant="h6" fontSize={isNarrowLayout ? 15 : 20} color={theme.colors.blue_primary}>
                                                    Observable behaviours
                                                </Typography>
                                            </button>
                                                    }
                                                    <button className={styles.tab} onClick={() => onTabSelect('summary')}
                                                        style={{
                                                            borderBottom: resolvedTab == 'summary' ? `3px solid var(--Stroke-Action, ${theme.colors.violet_primary})` : '',
                                                            backgroundColor: resolvedTab == 'summary' ? `var(--Components-Tab-Selected, ${theme.colors.tab_white})` : '',
                                                        }}
                                                    >
                                                        <Typography variant="h6" fontSize={isNarrowLayout ? 15 : 20} color={theme.colors.blue_primary}>
                                                    Summary
                                                        </Typography>
                                                    </button>
                                                    <button className={styles.tab} onClick={() => onTabSelect('details')}
                                                        style={{
                                                            borderBottom: resolvedTab == 'details' ? `3px solid var(--Stroke-Action, ${theme.colors.violet_primary})` : '',
                                                            backgroundColor: resolvedTab == 'details' ? `var(--Components-Tab-Selected, ${theme.colors.tab_white})` : '',
                                                        }}
                                                    >
                                                        <Typography variant="h6" fontSize={isNarrowLayout ? 15 : 20} color={theme.colors.blue_primary}>
                                                    Details
                                                        </Typography>
                                                    </button>
                                                </div>
                                            </div>
                                            {resolvedTab == 'behaviour' &&
                                        <GradingTab />
                                            }
                                            {resolvedTab == 'summary' &&
                                        <ExecutionSummary />
                                            }
                                            {resolvedTab == 'details' &&
                                        <ExecutionDetails />
                                            }
                                        </div>
                                    );
                                })()
                        }
                    </>
                )}
            </Observer>
        </>
    )
}